import { Typography } from '@/components/primitives/Typography';
import {
  createPurchaseFlowNonTripOfferBooking,
  createPurchaseFlowTripOfferBooking,
  getPurchaseFlowBookingById,
} from '@/features/purchase/purchaseActions';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import type { CreateBookingResponseDTO } from '@/types/dto/agent';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const useBookingCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentSearchFormValues } = useSearchWidget();

  const handleBookingCreationError = useCallback((error: ApiErrorResponse) => {
    const reason = error.response?.data.errors?.[0]?.message;

    toast.error(
      <div className="flex gap-1">
        <TransText i18nKey="failedToCreateBooking" />
        {reason && <Typography>{reason}</Typography>}
      </div>
    );
  }, []);

  const fetchBookingDetailsById = useCallback(
    async (createBookingResponse: CreateBookingResponseDTO) => {
      const id = createBookingResponse.booking?.id;

      if (id) {
        await dispatch(getPurchaseFlowBookingById(id)).unwrap();

        navigate('/purchase/checkout');
      }
    },
    [dispatch, navigate]
  );

  const handleCreatingAndFetchingTripOfferBooking = useCallback(async () => {
    try {
      const booking = await dispatch(
        createPurchaseFlowTripOfferBooking(
          currentSearchFormValues?.passengers ?? []
        )
      ).unwrap();

      await fetchBookingDetailsById(booking);
    } catch (e) {
      handleBookingCreationError(e as ApiErrorResponse);
    }
  }, [
    currentSearchFormValues?.passengers,
    dispatch,
    fetchBookingDetailsById,
    handleBookingCreationError,
  ]);

  const handleCreatingAndFetchingNonTripOfferBooking = useCallback(async () => {
    try {
      const booking = await dispatch(
        createPurchaseFlowNonTripOfferBooking(
          currentSearchFormValues?.passengers ?? []
        )
      ).unwrap();

      await fetchBookingDetailsById(booking);
    } catch (e) {
      handleBookingCreationError(e as ApiErrorResponse);
    }
  }, [
    currentSearchFormValues?.passengers,
    dispatch,
    fetchBookingDetailsById,
    handleBookingCreationError,
  ]);

  return {
    handleCreatingAndFetchingTripOfferBooking,
    handleCreatingAndFetchingNonTripOfferBooking,
  };
};
